import React, { useEffect, useState } from "react";
import { X, Loader2, Image as ImageIcon, Check } from "lucide-react";
import {
  fetchProjectAssetsByType,
  getAssetSignedUrl,
  type ProjectAsset,
} from "../utils/projectAssets";

export type LibraryImageKind = "first" | "last" | "ref";

interface LibraryImageGridProps {
  kind: LibraryImageKind;
  projectId: string | null;
  assetPreviewUrls: Record<string, string>;
  setAssetPreviewUrls: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  /** ids ya seleccionados (para resaltar). first/last: 0-1; ref: varios. */
  selectedIds: string[];
  /** el usuario eligió/toggle una imagen */
  onPick: (assetId: string) => void;
  /** abrir el panel Archivos completo en modo selección */
  onViewAll: () => void;
  /** cerrar la grilla sin elegir */
  onClose: () => void;
}

const titleFor = (kind: LibraryImageKind): string =>
  kind === "first"
    ? "Elegí el frame inicial"
    : kind === "last"
      ? "Elegí el frame final"
      : "Elegí imágenes de referencia";

// Grilla inline (en la sidebar) de las imágenes del proyecto. Reemplaza a la
// modal flotante: misma lógica de fetch + thumbnails, pero embebida en el panel.
const LibraryImageGrid: React.FC<LibraryImageGridProps> = ({
  kind,
  projectId,
  assetPreviewUrls,
  setAssetPreviewUrls,
  selectedIds,
  onPick,
  onViewAll,
  onClose,
}) => {
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const rows = await fetchProjectAssetsByType(projectId, "image");
        if (cancelled) return;
        setAssets(rows);
        const missing = rows.filter((a) => !assetPreviewUrls[a.id]);
        if (missing.length > 0) {
          const resolved = await Promise.all(
            missing.map(async (a) => {
              try {
                const url = await getAssetSignedUrl(a.storage_path);
                return url ? ([a.id, url] as const) : null;
              } catch {
                return null;
              }
            }),
          );
          if (cancelled) return;
          const updates: Record<string, string> = {};
          for (const r of resolved) if (r) updates[r[0]] = r[1];
          if (Object.keys(updates).length > 0) {
            setAssetPreviewUrls((prev) => ({ ...prev, ...updates }));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, assetPreviewUrls, setAssetPreviewUrls]);

  return (
    <div className="mt-2 rounded-lg border border-neutral-200 bg-surface-card p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-medium text-brand-black/80">
          {titleFor(kind)}
        </span>
        <button
          onClick={onClose}
          aria-label="Cerrar"
          className="w-6 h-6 flex items-center justify-center rounded-full text-brand-black/50 hover:text-brand-black/80 hover:bg-neutral-100 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <ImageIcon className="w-8 h-8 text-neutral-300 mb-2" />
          <p className="text-[13px] text-brand-black/80 font-medium">
            Todavía no tenés imágenes en este proyecto
          </p>
          <p className="text-[12px] text-neutral-500 max-w-xs leading-relaxed mt-1">
            Generá una con IA o subí desde el panel Archivos.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
          {assets.map((asset) => {
            const previewUrl = assetPreviewUrls[asset.id];
            const selected = selectedIds.includes(asset.id);
            return (
              <button
                key={asset.id}
                onClick={() => onPick(asset.id)}
                className={`relative aspect-square rounded-md overflow-hidden border transition-colors ${
                  selected
                    ? "border-brand-primary ring-2 ring-brand-primary"
                    : "border-neutral-200 hover:border-brand-primary"
                }`}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={asset.name || "Imagen"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-neutral-100">
                    <ImageIcon className="w-5 h-5 text-neutral-300" />
                  </div>
                )}
                {selected && (
                  <span className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full bg-brand-primary">
                    <Check className="w-2.5 h-2.5 text-white" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={onViewAll}
        className="mt-2 text-[12px] text-brand-primary hover:underline"
      >
        Ver todas en Archivos →
      </button>
    </div>
  );
};

export default LibraryImageGrid;
